import React from "react";
import { Loader2 } from "lucide-react";
import EntityAvatar from "components/Entities/EntityAvatar";
import type { QuizDetail } from "types/quiz";
import type { QuizUiStrings } from "i18n/quizUi";
import { getQuizButtonClassName, mapQuizUserToEntityAvatarUser } from "./quizUtils";

interface QuizWaitingAction {
  label: string;
  onClick: () => void;
  loading?: boolean;
  disabled?: boolean;
  helperText?: string;
}

type QuizWaitingBadgeMode = "progress" | "ready-for-part1" | "ready-for-part2";

interface QuizWaitingProps {
  ui: QuizUiStrings;
  quiz: QuizDetail;
  title: string;
  description: string;
  primaryAction?: QuizWaitingAction;
  secondaryAction?: QuizWaitingAction;
  badgeMode?: QuizWaitingBadgeMode;
}

/**
 * Shared waiting-state surface for lobby/phase transitions/results.
 */
const QuizWaiting: React.FC<QuizWaitingProps> = ({
  ui,
  quiz,
  title,
  description,
  primaryAction,
  secondaryAction,
  badgeMode = "progress",
}) => {
  const renderParticipantBadges = (participant: QuizDetail["participants"][number]): React.ReactNode => {
    if (badgeMode === "ready-for-part1") {
      return participant.part1_completed_at ? (
        <span className="rounded-full bg-green-600/10 px-2 py-1 text-xs text-green-700 dark:text-green-300">
          {ui.readyLabel}
        </span>
      ) : null;
    }

    if (badgeMode === "ready-for-part2") {
      return participant.part2_completed_at ? (
        <span className="rounded-full bg-green-600/10 px-2 py-1 text-xs text-green-700 dark:text-green-300">
          {ui.readyLabel}
        </span>
      ) : null;
    }

    return (
      <>
        {participant.part1_completed_at ? (
          <span className="rounded-full bg-green-600/10 px-2 py-1 text-xs text-green-700 dark:text-green-300">
            1
          </span>
        ) : null}
        {participant.part2_completed_at ? (
          <span className="rounded-full bg-eoe-blue/10 px-2 py-1 text-xs text-eoe-blue">
            2
          </span>
        ) : null}
      </>
    );
  };

  return (
    <div className="mx-auto flex w-full max-w-4xl flex-col gap-6 px-4 py-8">
      <div className="space-y-2">
        <h1 className="text-3xl font-semibold text-eoe-text-heading dark:text-eoe-dark-text-heading">{title}</h1>
        <p className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">{description}</p>
      </div>

      <section className="rounded-2xl border border-eoe-border-main bg-eoe-bg-modal p-5 dark:border-eoe-dark-border-main dark:bg-eoe-dark-bg-modal">
        <h2 className="mb-4 text-base font-semibold text-eoe-text dark:text-eoe-dark-text">{ui.joinedPlayersTitle}</h2>
        <div className="space-y-3">
          {quiz.participants.map((participant) => (
            <div
              key={String(participant.user_id ?? participant.joined_at ?? "participant")}
              className="flex items-center justify-between gap-3 rounded-2xl border border-eoe-border-main bg-eoe-bg-hover px-3 py-3 dark:border-eoe-dark-border-main dark:bg-eoe-dark-bg-hover"
            >
              <EntityAvatar
                entity={mapQuizUserToEntityAvatarUser(participant.user)}
                entityType="user"
                entityId={participant.user_id ?? undefined}
                entityChip={true}
                showLink={true}
                isInputField={false}
                className="min-w-0 flex-1"
              />
              <div className="flex flex-wrap justify-end gap-2">
                {renderParticipantBadges(participant)}
              </div>
            </div>
          ))}
        </div>
      </section>

      {primaryAction || secondaryAction ? (
        <div className="space-y-2">
          <div className="flex flex-col justify-end gap-3 sm:flex-row">
            {secondaryAction ? (
              <button
                type="button"
                onClick={secondaryAction.onClick}
                disabled={secondaryAction.disabled || secondaryAction.loading}
                className={getQuizButtonClassName({
                  variant: "secondary",
                  disabled: Boolean(secondaryAction.disabled || secondaryAction.loading),
                })}
              >
                {secondaryAction.loading ? <Loader2 className="size-4 animate-spin" aria-hidden /> : null}
                {secondaryAction.label}
              </button>
            ) : null}
            {primaryAction ? (
              <button
                type="button"
                onClick={primaryAction.onClick}
                disabled={primaryAction.disabled || primaryAction.loading}
                className={getQuizButtonClassName({
                  disabled: Boolean(primaryAction.disabled || primaryAction.loading),
                })}
              >
                {primaryAction.loading ? <Loader2 className="size-4 animate-spin" aria-hidden /> : null}
                {primaryAction.label}
              </button>
            ) : null}
          </div>
          {primaryAction?.helperText ? (
            <p className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
              {primaryAction.helperText}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default QuizWaiting;
